// Servicio de verificación de identidad (V2 — WhatsApp OTP).
//
// Réplica de los endpoints que usa la app Flutter `flutterUXS/` para el flow
// de Onboarding V2 con verificación diferida:
//
//   pOSTEnviarCodigoWhatsappCall   → send_whatsapp_verification_code
//   pOSTValidarCodigoWhatsappCall  → validate_whatsapp_verification_code
//   pOSTChangeUserPhoneCall        → change_user_mobile
//
// Documentación canónica:
//   - /Users/ricardo/Work/_vault/Daisies/flutterUXS-Onboarding-V2.md
//   - flutterUXS/docs/FLUJO_REGISTRO_V2.md §5.7, §5.8, §6
//
// Patrón V2: la verificación SÓLO se exige cuando la usuaria intenta agendar
// o comprar membresía. El backend devuelve `result.message: 'PHONE_NOT_VERIFIED'`
// y el cliente intercepta, abre el modal de WhatsApp, y reintenta la llamada
// original tras validar el código.

import { getFreshIdToken } from './FirebaseService';

const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN;
const ACCOUNT_ID = parseInt(process.env.REACT_APP_ACCOUNT_ID, 10) || 1;

// Los ID tokens de Firebase expiran a la hora. El authToken que llega de los
// callers suele venir de la sesión persistida (localStorage/cookie) y puede
// estar vencido — Odoo responde "Permission not allowed by oauth". Renovamos
// siempre que haya sesión Firebase viva; el recibido queda solo como fallback
// (sesiones bootstrapeadas por bridge sin currentUser).
const resolveAuthToken = async (authToken) => (await getFreshIdToken()) || authToken || null;

const post = async (payload) => {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return response.json();
};

// Marcador exacto del gate (NO traducir — viene literal del backend).
export const PHONE_NOT_VERIFIED = 'PHONE_NOT_VERIFIED';

// Detecta si una respuesta de booking trae el gate de phone-not-verified.
// El backend puede empaquetarlo de varias formas según el endpoint:
//   - result.message === 'PHONE_NOT_VERIFIED' (REST /appointment/)
//   - result.status === 'error' + result.message === 'PHONE_NOT_VERIFIED'
//   - error.data.message === 'PHONE_NOT_VERIFIED' (envuelto como AccessError)
export const isPhoneNotVerifiedResponse = (resp) => {
  if (!resp || typeof resp !== 'object') return false;
  const msg = resp?.result?.message ?? resp?.error?.data?.message ?? resp?.message;
  if (typeof msg === 'string' && msg.includes(PHONE_NOT_VERIFIED)) return true;
  // También verificamos `error: { ... }` que puede traer el código en `arguments`.
  const args = resp?.error?.data?.arguments;
  if (Array.isArray(args) && args.some(a => typeof a === 'string' && a.includes(PHONE_NOT_VERIFIED))) {
    return true;
  }
  return false;
};

// =================== ENVIAR CÓDIGO POR WHATSAPP ===================
// Réplica de `pOSTEnviarCodigoWhatsappCall`.
// Manda un OTP de 4 dígitos al WhatsApp del usuario y devuelve `notification_id`
// que luego se usa para validar.
export const sendWhatsappVerificationCode = async ({ authToken, userId }) => {
  const token = await resolveAuthToken(authToken);
  if (!token || !userId) {
    return { success: false, error: 'Falta authToken o userId.' };
  }
  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: token,
      res_id: Number(userId),
      res_model: 'dais.user',
      res_method: 'send_whatsapp_verification_code',
      res_params: {},
    },
  };
  try {
    const data = await post(payload);
    const result = data?.result || {};
    // El backend devuelve el payload doblemente envuelto:
    // `result.data.data.notification_id` (forma canónica que lee Flutter en
    // api_calls.dart §$.result.data.data.notification_id). Toleramos también
    // el formato plano por si el endpoint se aplana en alguna versión.
    const notificationId =
      result?.data?.data?.notification_id ?? result?.data?.notification_id ?? null;
    if (result.success && notificationId != null) {
      return {
        success: true,
        notificationId: Number(notificationId),
      };
    }
    return {
      success: false,
      error: result.message || data?.error?.data?.message || 'No se pudo enviar el código.',
    };
  } catch (err) {
    console.warn('[authVerificationService.sendWhatsappVerificationCode] error', err);
    return { success: false, error: err.message };
  }
};

// =================== VALIDAR CÓDIGO ===================
// Réplica de `pOSTValidarCodigoWhatsappCall`.
// Valida el OTP de 4 dígitos. Si OK, el backend marca `phone_verified=true`
// (idempotente — el cliente debe refrescar su estado local tras éxito).
export const validateWhatsappVerificationCode = async ({
  authToken,
  userId,
  code,
  notificationId,
}) => {
  const token = await resolveAuthToken(authToken);
  if (!token || !userId || !code || !notificationId) {
    return { success: false, error: 'Parámetros incompletos para validar el código.' };
  }
  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: token,
      res_id: Number(userId),
      res_model: 'dais.user',
      res_method: 'validate_whatsapp_verification_code',
      res_params: {
        code: String(code).trim(),
        notification_id: Number(notificationId),
      },
    },
  };
  try {
    const data = await post(payload);
    const result = data?.result || {};
    if (result.success) {
      // Por consistencia con send_whatsapp_verification_code, toleramos shape
      // plano y doblemente envuelto.
      const inner = result.data?.data ?? result.data ?? {};
      return {
        success: true,
        phoneVerified: inner.phone_verified === true,
        stepState: inner.step_state || null,
      };
    }
    return {
      success: false,
      error: result.message || data?.error?.data?.message || 'Código inválido.',
    };
  } catch (err) {
    console.warn('[authVerificationService.validateWhatsappVerificationCode] error', err);
    return { success: false, error: err.message };
  }
};

// =================== CAMBIAR TELÉFONO ===================
// Réplica de `pOSTChangeUserPhoneCall`. Actualiza el teléfono en `dais.user` y
// **invalida** `phone_verified` — la usuaria tendrá que verificar de nuevo.
export const changeUserPhone = async ({ authToken, userId, mobile, countryPhoneId }) => {
  const token = await resolveAuthToken(authToken);
  if (!token || !userId || !mobile) {
    return { success: false, error: 'Parámetros incompletos para cambiar teléfono.' };
  }
  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: token,
      res_id: Number(userId),
      res_model: 'dais.user',
      res_method: 'change_user_mobile',
      res_params: {
        mobile: String(mobile).trim(),
        // Nunca null/0: rompe el envío del WhatsApp. Default a México (156) como respaldo.
        country_phone_id: countryPhoneId ? Number(countryPhoneId) : 156,
      },
    },
  };
  try {
    const data = await post(payload);
    const result = data?.result || {};
    if (result.success) {
      return { success: true };
    }
    return {
      success: false,
      error: result.message || data?.error?.data?.message || 'No se pudo cambiar el teléfono.',
    };
  } catch (err) {
    console.warn('[authVerificationService.changeUserPhone] error', err);
    return { success: false, error: err.message };
  }
};

const authVerificationService = {
  sendWhatsappVerificationCode,
  validateWhatsappVerificationCode,
  changeUserPhone,
  isPhoneNotVerifiedResponse,
  PHONE_NOT_VERIFIED,
};

export default authVerificationService;
